import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ListLayout } from 'components/common/ListLayout';
import TableHeaderItem from 'components/common/ListLayout/TableHeaderItem';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import { PAGELENGTH } from 'components/common/ListLayout';
import { getEyeStyle } from "components/common/TableColor";
import { fetchAllLanguageRequests, addLanguageRequest, updateLanguageRequest, approveLanguageRequest, rejectLanguageRequest, languageRequestSelector } from 'slices/languageRequestSlice';
import { Controller, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { getBackgroundStyle } from 'components/common/TableColor';
import moment from "moment";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function LanguageRequests() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { languageRequests, last_page: totalPages, loader, total, current_page, per_page: pageLength } = useSelector(languageRequestSelector);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLengthState, setPageLengthState] = useState(PAGELENGTH);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'id', sortDirection: 'desc' });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEditRequest, setSelectedEditRequest] = useState(null);
  const [editName, setEditName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [viewCancelModalOpen, setViewCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const { handleSubmit, control, reset } = useForm({ defaultValues: { language_name: '' } });

  useEffect(() => {
    dispatch(fetchAllLanguageRequests(1, { pageLength: PAGELENGTH }));
  }, []);

  const onSubmit = (data) => {
    dispatch(addLanguageRequest(data, (status, message) => {
      if (status) {
        toast.success(message || 'Request submitted');
        reset();
        dispatch(fetchAllLanguageRequests(1, { pageLength: pageLengthState }));
      } else {
        toast.error(message || 'Unable to submit');
      }
    }));
  };

  const renderFilters = () => (
    <div className="p-3 w-100">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row-primary" style={{ marginBottom: 10 }}>
          <div className="row-primary" style={{ width: '100%' }}>
            <h2>{t("translation.languageName")}</h2>
            <Controller
              name="language_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <input type="text" className="input-in type_inp" placeholder={t("translation.languageNamePlaceholder")} {...field} />
              )}
            />
          </div>
        </div>
        <div className="row-primary btns_area row_10 mb-0">
          <SubmitButton handleSubmit={() => handleSubmit(onSubmit)()} buttonType="button" btnText={t("translation.requestLanguage")} className="green" />
        </div>
      </form>
    </div>
  );

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedEditRequest(null);
    setEditName('');
  };

  const openRejectModal = (item) => {
    setSelectedEditRequest(item);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setSelectedEditRequest(null);
    setRejectReason('');
  };

  const openViewCancelModal = (item) => {
    setCancelReason(item?.request_details?.cancel_reason || '');
    setViewCancelModalOpen(true);
  };

  const closeViewCancelModal = () => {
    setViewCancelModalOpen(false);
    setCancelReason('');
  };

  const refreshList = () => {
    dispatch(
      fetchAllLanguageRequests(currentPage, {
        pageLength: pageLengthState,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  };

  const handleEditSubmit = () => {
    if (!selectedEditRequest) return;
    setEditLoading(true);
    dispatch(
      updateLanguageRequest(selectedEditRequest.id, { language_name: editName }, (status, message) => {
        if (status) {
          toast.success(message || 'Language request updated');
          closeEditModal();
          refreshList();
        } else {
          toast.error(message || 'Unable to update language request');
        }
        setEditLoading(false);
      })
    );
  };

  const handleApprove = async (itemId) => {
    // const result = await Swal.fire({
    //   title: 'Are you sure you want to Move To Client Request?',
    //   icon: 'question',
    //   showCancelButton: true,
    //   confirmButtonText: 'OK',
    //   cancelButtonText: 'Cancel',
    //   confirmButtonColor: '#096171',
    //   cancelButtonColor: '#dc3545',
    // });

    // if (result.isConfirmed) {
      setActionLoading(true);
      dispatch(
        approveLanguageRequest(itemId, (status, message) => {
          if (status) {
            toast.success(message || 'Language request moved to client request');
            navigate('/appointment-requests');
            // refreshList();
          } else {
            toast.error(message || 'Unable to move request');
          }
          setActionLoading(false);
        })
      );
    // }
  };

  const handleRejectSubmit = () => {
    if (!selectedEditRequest) return;
    if (!rejectReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }

    setActionLoading(true);
    dispatch(
      rejectLanguageRequest(selectedEditRequest.id, { reason: rejectReason }, (status, message) => {
        if (status) {
          toast.success(message || 'Language request cancelled');
          closeRejectModal();
          refreshList();
        } else {
          toast.error(message || 'Unable to cancel request');
        }
        setActionLoading(false);
      })
    );
  };

  const renderTableHeader = () => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(
        fetchAllLanguageRequests(currentPage, {
          pageLength: pageLengthState,
          sortBy,
          sortDirection: dir,
        })
      );
    };

    return (
      <tr>
        <th>#</th>
        <th>
          <TableHeaderItem title={t("translation.appRequestId")} sortBy="appid" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title={t("translation.status")} sortBy="status" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
            <TableHeaderItem
                title={t("table.service")}
                sortBy="type"
                sortDirection={sortConfig.sortDirection}
                callBack={callBack}
            />
        </th>
        <th>
            <TableHeaderItem
                title={t("table.date")}
                sortBy="date"
                sortDirection={sortConfig.sortDirection}
                callBack={callBack}
            />
        </th>
        <th>
          <TableHeaderItem title={t("table.language")} sortBy="language_name" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title={t("table.client")} sortBy="client_name" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
            <TableHeaderItem
                title={t("table.subAccount")}
                sortBy="subclient_id"
                sortDirection={sortConfig.sortDirection}
                callBack={callBack}
            />
        </th>
        {/* <th>Action</th> */}
      </tr>
    );
  };

  const renderTableContent = () => {
    const pageIndex = Number(current_page || currentPage || 1);
    const lengthPerPage = Number(pageLength || pageLengthState || PAGELENGTH);

    return languageRequests?.map((item, idx) => {
      const style = getBackgroundStyle(item?.status);
      return (
        <tr key={item.id || idx} style={style}>
          {/* <td style={style}>{idx + 1 + (pageIndex - 1) * lengthPerPage}</td> */}
          <td className='p-0' style={style}>
              <div className={`eye_area  ${item?.appointment_details?.extra_mileage_request == 1 && 'extra-mileage'}`} style={getEyeStyle(item?.status, style)}>
                  <Link to={`/language-requests/view/${item?.id}`}><i class="fa-solid fa-eye"></i></Link>
              </div>
          </td>
          <td style={style}>
                {item?.appid || 'N/A'}
          </td>
          <td style={style}>{item?.status == 'cancelled' ? item?.request_details?.cancelled_by == 'mb' ? 'cancelled by MB' : 'cancelled' : item?.status}</td>
          <td style={style}>{item?.type || 'N/A'}</td>
          <td style={style}>{moment(item?.datetime, 'YYYY-MM-DD hh:mm A').format('MMM D, YYYY hh:mm A') || 'N/A'}</td>
          <td style={style}>{item?.language?.name ? item?.language?.name : item?.language_requests[0]?.language_name || 'N/A'}</td>
          <td style={style}>{item?.accounts?.user?.name || 'N/A'}</td>
          <td style={style}>{item?.subclient?.subaccount_name || 'N/A'}</td>
          {/* <td style={style}>
            <div className="row-primary" style={{ gap: 8 }}>
            {item?.status !== 'approved' && item?.status !== 'cancelled' && (
            <>
            <button type="button" className="main_button" style={{ padding: '12px 14px' }} onClick={() => handleApprove(item.id)} disabled={actionLoading} title="Move To Client Request"> 
              <i className="fa-solid fa-globe"></i>
            </button>
            <button type="button" className="table_delete btn-sm" style={{ padding: '12px 14px' }} onClick={() => openRejectModal(item)} disabled={actionLoading} title="Cancel Request">
              <i className="fa-solid fa-circle-xmark"></i> 
            </button>
            </>
            )}
            {item?.status == 'cancelled' &&item?.request_details?.cancel_reason && (
              <button
                type="button"
                className="main_button green"
                style={{ padding: '12px 14px' }}
                onClick={() => openViewCancelModal(item)}
                title="View Cancel Reason"
              >
                <i className="fa-solid fa-eye"></i>
              </button>
            )}
            </div>
          </td> */}
        </tr>
      );
    });
  };

  return (
    <>
      <ListLayout
        heading={t("translation.languageRequests")}
        title=""
        addButtonDisabled={true}
        disableBootstrap={true}
        renderTableHeader={renderTableHeader}
        renderTableContent={renderTableContent}
        // renderFilters={renderFilters}
        totalPages={totalPages}
        fetchList={fetchAllLanguageRequests}
        loader={loader}
        noOfItems={total}
        hideHeader={false}
        searchPlaceholder={t("translation.searchLanguageRequests")}
        hidePagination={false}
        pageLengthChange={(len) => setPageLengthState(len)}
        paginationParams={{}}
        defaultPageLength={PAGELENGTH}
      />

      <Modal isOpen={editModalOpen} toggle={closeEditModal} centered className="modal-medium">
        <ModalHeader toggle={closeEditModal}>{t("translation.editLanguageRequest")}</ModalHeader>
        <ModalBody className="modal_form_body">
          <div className="row-primary">
            <h2>Language Name</h2>
            <input
              type="text"
              className="input-in type_inp"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </div>
        </ModalBody>
        <ModalFooter className="row_shift Step_change modal-footer">
          <button type="button" className="main_button" onClick={handleEditSubmit} disabled={editLoading || !editName.trim()}>
            {editLoading ? 'Updating...' : 'Update'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={rejectModalOpen} toggle={closeRejectModal} centered className="modal-medium">
        <ModalHeader toggle={closeRejectModal}><h5 className="modal-title_u">{t("translation.cancelLanguageRequest")}</h5></ModalHeader>
        <ModalBody className="modal_form_body">
          <div className="min-height-auto" style={{ backgroundColor: 'rgb(242, 242, 242)', padding: '10px', borderRadius: '8px' }}>
            <h2>{t("translation.appointmentId")}: {selectedEditRequest?.appid}</h2>
            <div className="row-primary">
            <h2>{t("translation.reason")}</h2>
            <textarea
              className="input-in type_inp"
              rows={5}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("translation.reasonForCancel")}
            />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="main_button  tomatoRed" onClick={handleRejectSubmit} disabled={actionLoading || !rejectReason.trim()}>
            {t("translation.statusCancelled")}
          </button>
          <button type="button" className="Cancle_button" onClick={closeRejectModal}>
            {t("translation.close")}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={viewCancelModalOpen} toggle={closeViewCancelModal} centered className="modal-medium">
        <ModalHeader class toggle={closeViewCancelModal}><h5 className="modal-title_u">{t("translation.cancelReason")}</h5></ModalHeader>
        <ModalBody className="modal_form_body">
          <div className="row-primary">
            <h2>Reason</h2>
            <div className="type_inp input-in pt-3" style={{ minHeight: 120, whiteSpace: 'pre-wrap' }}>
              {cancelReason}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="row_shift Step_change modal-footer">
          <button type="button" className="Cancle_button" onClick={closeViewCancelModal}>
            {t("translation.close")}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default LanguageRequests;
